import { Component, ElementRef, forwardRef, Input, OnChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { provideTranslocoScope } from '@ngneat/transloco';
import { BlurCallbackLink, TypedChanges } from '@platform-frontend/util';
import { UI_TOOLS_TRANSLOCO_SCOPE } from '../../ui-tools-transloco-scope';
import { FileType } from '../file-type';

@Component({
  selector: 'yat-file-select',
  templateUrl: './file-select.component.html',
  styleUrls: ['./file-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileSelectComponent),
      multi: true
    },
    provideTranslocoScope(UI_TOOLS_TRANSLOCO_SCOPE)
  ]
})
export class FileSelectComponent implements ControlValueAccessor, OnChanges {
  @ViewChild('panel')
  panelEl: ElementRef<HTMLDivElement>;

  /** maximum file size in mb */
  @Input()
  maxFileSize: number = 1;

  @Input()
  fileTypes: FileType[] | null = null;

  @Input()
  additionalFileInfo: string | null = null;

  readonly selectFileLink: BlurCallbackLink = {
    class: 'link link-primary link-large',
    onBlur: () => this.onTouched(),
    id: '7b61a93b-c3bf-4167-bb7c-ce216b38ce7e'
  };

  fileExtensions: string[] | null = null;

  selectedFile: File | null = null;

  warning: null | 'wrongFileType' | 'maxFileSizeExceeded' = null;

  isDragged: boolean = false;

  onChange: (newSelectedFile: File | null) => void;

  onTouched: () => void;

  registerOnChange(onChangeCallback: (newSelectedFile: File | null) => void): void {
    this.onChange = onChangeCallback;
  }

  registerOnTouched(onTouchedCallback: () => void): void {
    this.onTouched = onTouchedCallback;
  }

  writeValue(newSelectedFile: File | null): void {
    this.selectedFile = newSelectedFile;
  }

  ngOnChanges(changes: TypedChanges<FileSelectComponent>): void {
    if (changes.fileTypes) {
      if (this.fileTypes !== null) {
        this.fileExtensions = this.fileTypes.slice();
        if (this.fileTypes.includes('jpg')) {
          this.fileExtensions.push('jpeg');
        }
      } else {
        this.fileExtensions = null;
      }
    }
  }

  onDragOver(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.isDragged = true;
  }

  onDragLeave(event: DragEvent): void {
    if (event.target === this.panelEl.nativeElement) {
      event.stopPropagation();
      event.preventDefault();
      this.isDragged = false;
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragged = false;
    const { dataTransfer } = event;
    if (dataTransfer) {
      if (dataTransfer.items) {
        if (dataTransfer.items[0].kind === 'file') {
          this.selectFile(dataTransfer.items[0].getAsFile());
        }
        dataTransfer.items.clear();
      } else if (dataTransfer.files) {
        this.selectFile(dataTransfer.files[0]);
        dataTransfer.clearData();
      }
    }
  }

  selectFileFromEventTarget(eventTarget: EventTarget | null): void {
    const fileList: FileList | null = eventTarget !== null ? (eventTarget as HTMLInputElement).files : null;
    const newSelectedFile: File | null = fileList ? fileList[0] : null;
    this.selectFile(newSelectedFile);
  }

  private selectFile(newSelectedFile: File | null): void {
    if (newSelectedFile !== null && this.fileExtensions !== null && !this.fileExtensions.includes(newSelectedFile.name.split('.').slice(-1)[0].toLowerCase())) {
      this.warning = 'wrongFileType';
    } else if (newSelectedFile !== null && newSelectedFile.size > this.maxFileSize * 1024 * 1024) {
      this.warning = 'maxFileSizeExceeded';
    } else {
      this.warning = null;
    }

    if (this.warning) {
      this.selectedFile = null;
    } else {
      this.selectedFile = newSelectedFile;
    }

    this.onChange(this.selectedFile);
    this.onTouched();
  }
}
